import { useState, useEffect } from "react";
import { User, Moon, Sun, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

export default function Profile() {
  const { user, setUser } = useAuth();
  const { dark, toggle } = useTheme();
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => console.error("Failed to load admin stats:", e));
  }, [isAdmin]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/profile", { name });
      setUser({ ...user, name });
      toast.success("Profile updated");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12" data-testid="profile-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">Settings</p>
      <h1 className="text-4xl font-bold tracking-tighter">Profile</h1>

      <div className="mt-8 rounded-3xl border border-border bg-card p-8 space-y-6">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
            <User className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="font-semibold">{user?.name}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="name">Display name</Label>
          <Input id="name" data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <Button data-testid="save-profile-btn" onClick={save} disabled={saving || !name.trim()} className="rounded-full glow-btn">
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>

      <div className="mt-6 rounded-3xl border border-border bg-card p-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {dark ? <Moon className="h-5 w-5 text-primary" /> : <Sun className="h-5 w-5 text-primary" />}
          <div>
            <p className="font-medium text-sm">Dark mode</p>
            <p className="text-xs text-muted-foreground">Switch between light and dark themes</p>
          </div>
        </div>
        <Switch data-testid="dark-mode-switch" checked={dark} onCheckedChange={toggle} />
      </div>

      {isAdmin && (
        <div className="mt-6 rounded-3xl border border-border bg-card p-8" data-testid="admin-stats-card">
          <div className="flex items-center gap-2 mb-6">
            <Users className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold tracking-tight">User signups</h2>
            <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded-full px-2 py-0.5">Admin</span>
          </div>
          {!stats ? (
            <p className="text-sm text-muted-foreground" data-testid="admin-stats-loading">Loading stats…</p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Total users", value: stats.total_users, testid: "stat-total-users" },
                  { label: "Last 24h", value: stats.signups_today, testid: "stat-today" },
                  { label: "Last 7 days", value: stats.signups_this_week, testid: "stat-week" },
                  { label: "Last 30 days", value: stats.signups_this_month, testid: "stat-month" },
                ].map((s) => (
                  <div key={s.testid} className="rounded-2xl border border-border bg-muted/30 p-4" data-testid={s.testid}>
                    <p className="text-3xl font-bold tracking-tighter">{s.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
              {stats.recent_users?.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Recent signups</p>
                  <div className="divide-y divide-border" data-testid="recent-signups-list">
                    {stats.recent_users.map((u, i) => (
                      <div key={`${u.email}-${i}`} className="flex items-center justify-between py-2.5" data-testid={`recent-signup-${i}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{u.name || u.email}</p>
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <p className="text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—"}</p>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{u.provider === "google" ? "Google" : "Email"}{u.role === "admin" ? " · Admin" : ""}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
